import * as errors from './common/errors';
import * as helpers from './common/helpers';
import * as schema from './common/schema';

import type { PropertyGetter, PropertyResolver } from './common/types';
import type { Event } from './event';

export type ClientSendOptions = {
  // Allows Query String to be passed, but it should be optional
  query?: string;
  /** @deprecated passing isBeforeUnload is deprecated and beacons should be used by default */
  // Beacons are used by default so isBeforeUnload is not needed anymore
  isBeforeUnload: boolean;
  // An Array of Events to be dispatched over the Network
  events: Event[];
  // Optional Handle for allowing to report back that a Request failed
  onFailure?: () => void;
};

export type ClientDependencies = {
  clientName?: string;
  getDebug: () => boolean;
  getEmail: PropertyGetter<string | null | undefined>;
  getHubId: PropertyGetter<number | null | undefined>;
  getHstc: PropertyGetter<string | null | undefined>;
  getLang: PropertyGetter<string | null | undefined>;
  getCurrentHref?: () => string;
  getReferrer?: () => string;
  getUserAgent?: () => string;
  getNetworkType?: () => string;
  getNetworkSpeed?: () => string;
  getScreenWidth?: () => number;
  getScreenHeight?: () => number;
  getWindowWidth?: () => number;
  getWindowHeight?: () => number;
  getDeployableName?: () => string;
  getDeployableVersion?: () => string;
  getHubSpotSessionId?: () => string;
  getTempStorage: (key: any) => string | null;
  logMessage?: (...m: any[]) => void;
  logWarning?: (...w: any[]) => void;
  logError?: (...e: any[]) => void;
  reportWarning?: (warning: Error, options?: any) => void;
  reportError?: (error: Error, options?: any) => void;
  send: (options: ClientSendOptions) => void;
  setTempStorage: (key: string, value: string) => void;
};

// Schema for createClient() function args
export const clientDependenciesSchema = schema.create(
  'client dependency',
  errors.configError,
  {
    clientName: {
      types: ['string'],
      default: 'custom',
    },
    getDebug: {
      types: ['function'],
    },
    getEmail: {
      types: ['function'],
    },
    getHubId: {
      types: ['function'],
    },
    getHstc: {
      types: ['function'],
    },
    getLang: {
      types: ['function'],
    },
    getCurrentHref: {
      types: ['function'],
      isOptional: true,
      default: () =>
        helpers.safeGetOrDefault(['window', 'location', 'href'], ''),
    },
    getReferrer: {
      types: ['function'],
      isOptional: true,
      default: () => helpers.safeGetOrDefault(['document', 'referrer'], ''),
    },
    getUserAgent: {
      types: ['function'],
      isOptional: true,
      default: () => helpers.safeGetOrDefault(['navigator', 'userAgent'], ''),
    },
    getNetworkType: {
      types: ['function'],
      isOptional: true,
      default: () =>
        helpers.safeGetOrDefault(
          ['navigator', 'connection', 'effectiveType'],
          ''
        ),
    },
    getNetworkSpeed: {
      types: ['function'],
      isOptional: true,
      default: () =>
        helpers.safeGetOrDefault(['navigator', 'connection', 'downlink'], ''),
    },
    getScreenWidth: {
      types: ['function'],
      isOptional: true,
      default: () => helpers.safeGetOrDefault(['screen', 'width'], ''),
    },
    getScreenHeight: {
      types: ['function'],
      isOptional: true,
      default: () => helpers.safeGetOrDefault(['screen', 'height'], ''),
    },
    getWindowWidth: {
      types: ['function'],
      isOptional: true,
      default: () => helpers.safeGetOrDefault(['window', 'innerWidth'], ''),
    },
    getWindowHeight: {
      types: ['function'],
      isOptional: true,
      default: () => helpers.safeGetOrDefault(['window', 'innerHeight'], ''),
    },
    getDeployableName: {
      types: ['function'],
      isOptional: true,
      default: () =>
        helpers.safeGetOrDefault(
          ['window', 'hubspot', 'bender', 'currentProject'],
          ''
        ),
    },
    getDeployableVersion: {
      types: ['function'],
      isOptional: true,
      default: () =>
        helpers.safeGetOrDefault(
          ['window', 'hubspot', 'bender', 'currentProjectVersion'],
          ''
        ),
    },
    getHubSpotSessionId: {
      types: ['function'],
      isOptional: true,
      default: () =>
        helpers.safeGetOrDefault(['window', 'hubspot', 'sessionId'], ''),
    },
    logMessage: {
      types: ['function'],
      isOptional: true,
      default: (...m: any[]) =>
        helpers.safeGetOrDefault(
          ['console', 'debug'],
          (...p: any[]) => p
        )(...m),
    },
    logWarning: {
      types: ['function'],
      isOptional: true,
      default: (...w: any[]) =>
        helpers.safeGetOrDefault(['console', 'warn'], (...p: any[]) => p)(...w),
    },
    logError: {
      types: ['function'],
      default: (...e: any[]) =>
        helpers.safeGetOrDefault(
          ['console', 'error'],
          (...p: string[]) => p
        )(...e),
    },
    reportError: {
      types: ['function'],
      isOptional: true,
      default: () => {},
    },
    reportWarning: {
      types: ['function'],
      isOptional: true,
      default: () => {},
    },
    getTempStorage: {
      types: ['function'],
    },
    setTempStorage: {
      types: ['function'],
    },
    send: {
      types: ['function'],
    },
  }
);

// Schema for createTracker() function args
export const trackerConfigSchema = schema.create(
  'config option',
  errors.configError,
  {
    trackerName: {
      types: ['string'],
      isOptional: true,
      default: 'default',
    },
    allowUnauthed: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
    bypassPool: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
    debug: {
      types: ['boolean', 'function'],
      isOptional: true,
    },
    events: {
      types: ['object'],
      isOptional: true,
      default: {},
    },
    isBeforeUnload: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
    isExternalHost: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
    lastKnownEventProperties: {
      types: ['array'],
      isOptional: true,
      default: [],
    },
    onError: {
      types: ['function'],
      isOptional: true,
    },
    onWarning: {
      types: ['function'],
      isOptional: true,
    },
    onScheduled: {
      types: ['function'],
      isOptional: true,
      default: () => {},
    },
    preserveTrackerProperties: {
      types: ['boolean'],
      isOptional: true,
      default: true,
    },
    preserveTrackerEvents: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
    properties: {
      types: ['object'],
      isOptional: true,
      default: {},
    },
    standalone: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
  }
);

// These properties comes from the client and are always present
// They're considered the default properties becuase of their nature
// They're also resolved during the `.track` process
export type DefaultTrackerProperties = {
  email: PropertyResolver<string | null>;
  hubId: PropertyResolver<number | null>;
  hstc: PropertyResolver<string | null>;
  lang: PropertyResolver<string | null>;
  deviceId: PropertyResolver<string | null>;
};

// // Schema for the Default Tracker Properties
// Those are Properties that can always be set in any Tracker instance
export const trackerPropertiesSchema = schema.create(
  'tracker properties',
  errors.configError,
  {
    email: {
      types: ['string', 'function', 'object', 'null'],
    },
    hubId: {
      types: ['number', 'function', 'object', 'null'],
    },
    hstc: {
      types: ['string', 'function', 'object', 'null'],
    },
    lang: {
      types: ['string', 'function', 'object', 'null'],
    },
    deviceId: {
      types: ['string', 'function', 'object', 'null'],
      isOptional: true,
      default: undefined,
    },
  },
  false
);

export type EventDefinitionMeta = {
  description?: string;
  owner?: string;
  isPublic?: boolean;
  imgSrc?: string;
};

export type EventDefinition = {
  name: string;
  namespace?: string;
  class?: string;
  version?: string;
  properties: Record<string, EventPropertyTypes | EventPropertySchema>;
  meta?: EventDefinitionMeta;
};

export type ResolvedEventDefinition = {
  name: string;
  namespace: string;
  class: string;
  version: string;
  properties: Record<string, EventPropertySchema>;
  meta?: EventDefinitionMeta;
};

export const eventClasses = [
  'activation',
  'creation',
  'error',
  'exposure',
  'funnel',
  'interaction',
  'none',
  'signup',
  'usage',
  'view',
  'warning',
] as const;

// Schema for events.yaml definitions
export const eventDefinitionSchema = schema.create(
  'event property',
  errors.configError,
  {
    name: {
      types: ['string'],
    },
    namespace: {
      types: ['string'],
      isOptional: true,
      default: '',
    },
    class: {
      types: ['string'],
      oneOf: [...eventClasses],
    },
    version: {
      types: ['string'],
      isOptional: true,
      default: 'v1',
    },
    properties: {
      types: ['object'],
      isOptional: true,
      default: {},
    },
    meta: {
      types: ['object'],
      isOptional: true,
    },
  }
);

// All the allowed types for Event Properties (property.type)
export const eventPropertyTypes = [
  'boolean',
  'string',
  'number',
  'array',
] as const;

export type EventPropertyTypes = (typeof eventPropertyTypes)[number];

// Schema for the Properties from an Event Definition
export type EventPropertySchema = {
  type: EventPropertyTypes | string[];
  isOptional: boolean;
  description?: string;
};

export const eventPropertySchema = schema.create(
  'event property',
  errors.configError,
  {
    type: {
      types: ['string', 'array'],
      oneOf: [...eventPropertyTypes],
    },
    isOptional: {
      types: ['boolean'],
      isOptional: true,
      default: false,
    },
    description: {
      types: ['string'],
      isOptional: true,
      default: '',
    },
  }
);
